import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { Bug, X, Send, Loader2, MessageSquareHeart, Camera, ImageOff } from "lucide-react";
import html2canvas from "html2canvas";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import FloatingSlot from "@/components/FloatingSlot";

const KIND_OPTIONS = [
  { key: "bug", label: "Bug", emoji: "🐛" },
  { key: "idea", label: "Idea", emoji: "💡" },
  { key: "praise", label: "Praise", emoji: "❤️" },
  { key: "other", label: "Other", emoji: "✳️" },
];

export default function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("bug");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  // Capture a screenshot of the current page while the dialog is hidden.
  // We temporarily close the modal so the user's actual page state is captured,
  // reopen automatically, and downscale to keep payloads small (< ~800KB).
  const captureScreenshot = async () => {
    setCapturing(true);
    setOpen(false);
    // Give the browser one paint tick to actually hide the modal before capturing.
    await new Promise(r => setTimeout(r, 250));
    try {
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: false,
        logging: false,
        scale: Math.min(1, 900 / (window.innerWidth || 1200)),   // downscale wide screens
        backgroundColor: null,
      });
      // Compress to JPEG @ 0.7 quality — usually 100-400 KB, plenty for a bug report.
      const dataUrl = canvas.toDataURL("image/jpeg", 0.7);
      setScreenshot(dataUrl);
      toast.success("Screenshot attached to feedback.");
    } catch (e) {
      toast.error("Could not capture screenshot on this browser.");
    } finally {
      setCapturing(false);
      setOpen(true);
    }
  };

  const submit = async () => {
    if (message.trim().length < 8) {
      toast.error("Please share a little more detail (min 8 chars).");
      return;
    }
    setSending(true);
    try {
      await api.post("/feedback", {
        kind,
        message: message.trim(),
        page: location.pathname + location.search,
        email: email.trim() || undefined,
        screenshot: screenshot || undefined,
      });
      toast.success("Thanks — your feedback landed with our team.");
      setMessage("");
      setEmail("");
      setScreenshot(null);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating launcher — bottom-left FAB slot (order 0, desktop only).
          Uses centralized safe-zone system; PendingSyncBadge stacks above at order 1. */}
      <FloatingSlot
        zone="bottom-left"
        order={0}
        size="badge"
        zIndex="badge"
        hideOnKeyboard={false}
        className="hidden md:block"
      >
        <button
          onClick={() => setOpen(true)}
          className="flex items-center gap-2 rounded-full bg-white border border-stone-200 shadow-lg px-4 py-2.5 text-sm font-bold text-stone-800 hover:border-[#E05E36] hover:text-[#E05E36] hover:-translate-y-0.5 transition-all"
          aria-label="Send feedback"
          data-testid="feedback-launcher-btn"
        >
          <Bug size={16} />
          <span>Report a bug</span>
        </button>
      </FloatingSlot>

      {open && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" style={{ zIndex: "var(--z-modal)" }}
          onClick={() => !sending && setOpen(false)}
          data-testid="feedback-dialog-backdrop"
        >
          <div
            className="w-full max-w-md bg-white rounded-3xl border border-stone-200 shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4"
            onClick={e => e.stopPropagation()}
            data-testid="feedback-dialog"
          >
            <div className="px-6 pt-5 pb-3 flex items-start justify-between">
              <div>
                <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-[#2D5A27]">
                  <MessageSquareHeart size={14} /> We&apos;re listening
                </div>
                <h3 className="heading text-xl font-extrabold text-stone-900 mt-1">
                  Tell us what&apos;s on your mind
                </h3>
                <p className="text-xs text-stone-500 mt-1">
                  Real feedback from neighbours makes Find2Near better every week.
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-1 rounded-full hover:bg-stone-100"
                disabled={sending}
                aria-label="Close feedback dialog"
                data-testid="feedback-close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="px-6 pb-6 space-y-4">
              {/* Kind selector */}
              <div className="grid grid-cols-4 gap-1.5">
                {KIND_OPTIONS.map(k => (
                  <button
                    key={k.key}
                    onClick={() => setKind(k.key)}
                    className={`rounded-xl border py-2 text-xs font-bold flex flex-col items-center gap-0.5 transition-colors ${
                      kind === k.key
                        ? "bg-[#E05E36]/10 border-[#E05E36] text-[#E05E36]"
                        : "bg-white border-stone-200 text-stone-700 hover:border-stone-300"
                    }`}
                    data-testid={`feedback-kind-${k.key}`}
                  >
                    <span className="text-lg leading-none">{k.emoji}</span>
                    <span>{k.label}</span>
                  </button>
                ))}
              </div>

              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500">
                  {kind === "bug" ? "What went wrong?" : kind === "idea" ? "What should we build?" : "Message"}
                </label>
                <Textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder={
                    kind === "bug"
                      ? "Steps to reproduce, expected vs actual behaviour..."
                      : kind === "idea"
                      ? "Describe your idea and the problem it solves..."
                      : "Share your thoughts..."
                  }
                  className="mt-1.5 rounded-xl min-h-[120px]"
                  maxLength={4000}
                  data-testid="feedback-message"
                />
                <div className="text-[10px] text-stone-400 text-right mt-1">
                  {message.length} / 4000
                </div>
              </div>

              {/* Screenshot section — thumb preview + capture/remove */}
              <div className="rounded-2xl border border-stone-200 bg-stone-50 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500">
                      Screenshot (optional)
                    </div>
                    <div className="text-xs text-stone-500 mt-0.5 leading-snug">
                      A picture is worth a thousand words — attach the current view.
                    </div>
                  </div>
                  {screenshot ? (
                    <div className="flex items-center gap-2">
                      <img
                        src={screenshot}
                        alt="Attached screenshot preview"
                        className="w-14 h-10 object-cover rounded-md border border-stone-200"
                        data-testid="feedback-screenshot-preview"
                      />
                      <button
                        onClick={() => setScreenshot(null)}
                        className="p-1.5 rounded-full hover:bg-red-50 text-red-600"
                        aria-label="Remove screenshot"
                        title="Remove screenshot"
                        data-testid="feedback-screenshot-remove"
                      >
                        <ImageOff size={14} />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={captureScreenshot}
                      disabled={capturing}
                      className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-white border border-stone-300 px-3 py-1.5 text-xs font-bold text-stone-700 hover:border-[#2D5A27] hover:text-[#2D5A27] disabled:opacity-60"
                      data-testid="feedback-screenshot-capture"
                    >
                      {capturing ? <Loader2 size={12} className="animate-spin" /> : <Camera size={12} />}
                      {capturing ? "Capturing…" : "Attach"}
                    </button>
                  )}
                </div>
              </div>

              {!user && (
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500">
                    Email (optional — so we can follow up)
                  </label>
                  <Input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="mt-1.5 rounded-xl"
                    data-testid="feedback-email"
                  />
                </div>
              )}

              <Button
                onClick={submit}
                disabled={sending || message.trim().length < 8}
                className="w-full rounded-full bg-[#2D5A27] hover:bg-[#24491f] gap-2"
                data-testid="feedback-submit"
              >
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {sending ? "Sending…" : "Send feedback"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
